/**
 * Service for loyalty directory API calls
 */

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Fetch enrolled venues with optional personalization and location
 * @param {Object} options - Options for fetching companies
 * @param {string} options.customerToken - Optional customer token for personalization
 * @param {Object} options.userLocation - Optional user location {lat, lng}
 * @returns {Promise<Object>} Companies and category order
 */
export async function fetchEnrolledVenues({ customerToken = null, userLocation = null } = {}) {
  let url;
  if (customerToken) {
    url = `${apiUrl}/loyalty/enrolled-venues/personalized?customer_token=${encodeURIComponent(customerToken)}`;
  } else {
    url = `${apiUrl}/loyalty/enrolled-venues`;
  }

  if (userLocation) {
    const separator = url.includes('?') ? '&' : '?';
    url += `${separator}user_lat=${userLocation.lat}&user_lng=${userLocation.lng}`;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch companies: ${response.status}`);
  }

  const data = await response.json();
  return {
    companies: data.companies || [],
    categoryOrder: data.category_order || []
  };
}

/**
 * Fetch venue offers
 * @param {number} venueId - The venue ID
 * @param {Object} options - Options for fetching offers
 * @param {boolean} options.isActive - Filter by active offers
 * @param {number} options.limit - Limit number of offers
 * @returns {Promise<Array>} Array of offers
 */
export async function fetchVenueOffers(venueId, { isActive = true, limit = 10 } = {}) {
  try {
    const response = await fetch(
      `${apiUrl}/coupon/venue/${venueId}/offers?is_active=${isActive}&limit=${limit}`
    );
    if (response.ok) {
      const data = await response.json();
      return data.offers || [];
    }
    return [];
  } catch (error) {
    console.error(`Failed to fetch offers for venue ${venueId}:`, error);
    return [];
  }
}

/**
 * Fetch offers for all venues
 * @param {Array} companies - Array of company objects with venue_id
 * @returns {Promise<Object>} Map of venueId to offers array
 */
export async function fetchAllVenueOffers(companies) {
  const offerPromises = companies.map(async (company) => {
    const offers = await fetchVenueOffers(company.venue_id);
    return { venueId: company.venue_id, offers };
  });

  const results = await Promise.all(offerPromises);
  const offersData = {};
  results.forEach(({ venueId, offers }) => {
    offersData[venueId] = offers;
  });

  return offersData;
}

/**
 * Check if customer is enrolled in a venue
 * @param {number} venueId - The venue ID
 * @param {string} email - Customer email
 * @returns {Promise<boolean>} True if enrolled
 */
export async function checkVenueEnrollment(venueId, email) {
  try {
    const response = await fetch(
      `${apiUrl}/loyalty/check-opt-in-email?venue_id=${venueId}&email=${encodeURIComponent(email)}`
    );
    if (response.ok) {
      const data = await response.json();
      return data.opted_in && data.venue_has_access;
    }
    return false;
  } catch (error) {
    console.error(`Failed to check enrollment for venue ${venueId}:`, error);
    return false;
  }
}

/**
 * Check enrollment status for all venues
 * @param {Array} companies - Array of company objects with venue_id
 * @param {string} email - Customer email
 * @returns {Promise<Object>} Map of venueId to enrollment status
 */
export async function checkAllEnrollments(companies, email) {
  const enrollmentPromises = companies.map(async (company) => {
    const enrolled = await checkVenueEnrollment(company.venue_id, email);
    return { venueId: company.venue_id, enrolled };
  });

  const results = await Promise.all(enrollmentPromises);
  return results.reduce((acc, { venueId, enrolled }) => {
    acc[venueId] = enrolled;
    return acc;
  }, {});
}

/**
 * Fetch customer coupon counts and expiration data by venue
 * @param {string} customerToken - Customer token
 * @returns {Promise<Object>} Object with counts and expirations by venue
 */
export async function fetchCouponCounts(customerToken) {
  try {
    const response = await fetch(
      `${apiUrl}/coupon/customer/${encodeURIComponent(customerToken)}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch coupons');
    }

    const data = await response.json();
    const coupons = data.coupons || [];

    const counts = {};
    const expirations = {};

    coupons.forEach(coupon => {
      const venueId = coupon.venue_id;

      if (coupon.status === 'ready' || coupon.status === 'active') {
        counts[venueId] = (counts[venueId] || 0) + 1;

        if (coupon.expires_at) {
          const expirationDate = new Date(coupon.expires_at);
          const today = new Date();
          const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));

          if (daysUntilExpiration > 0) {
            if (!expirations[venueId] || daysUntilExpiration < expirations[venueId]) {
              expirations[venueId] = daysUntilExpiration;
            }
          }
        }
      }
    });

    return { counts, expirations };
  } catch (error) {
    console.error('Failed to fetch coupon counts:', error);
    return { counts: {}, expirations: {} };
  }
}

/**
 * Fetch trending events
 * @param {number} limit - Maximum number of events to return
 * @returns {Promise<Array>} Array of upcoming events
 */
export async function fetchTrendingEvents(limit = 10) {
  try {
    const response = await fetch(`${apiUrl}/loyalty/events?status=published&upcoming_only=true`);
    if (!response.ok) {
      throw new Error('Failed to fetch events');
    }

    const data = await response.json();
    const allEvents = data.events || [];

    // Filter to only active/upcoming events (hide only after end time has passed)
    const now = new Date();
    const upcomingEvents = allEvents.filter(event => {
      const endDate = new Date(event.end_at);
      return endDate > now;
    });

    // Deduplicate by event_id or slug
    const uniqueEventsMap = new Map();
    upcomingEvents.forEach(event => {
      const key = event.event_id || event.slug;
      if (!uniqueEventsMap.has(key)) {
        uniqueEventsMap.set(key, event);
      }
    });

    return Array.from(uniqueEventsMap.values()).slice(0, limit);
  } catch (error) {
    console.error('Failed to fetch trending events:', error);
    return [];
  }
}

/**
 * Fetch venue distances from user location
 * @param {Object} userLocation - User location {lat, lng}
 * @param {string} customerToken - Optional customer token
 * @returns {Promise<Object>} Map of venueId to distance info
 */
export async function fetchVenueDistances(userLocation, customerToken = null) {
  try {
    let url = `${apiUrl}/loyalty/enrolled-venues/distances?user_lat=${userLocation.lat}&user_lng=${userLocation.lng}`;

    if (customerToken) {
      url += `&customer_token=${encodeURIComponent(customerToken)}`;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch distances: ${response.status}`);
    }

    const data = await response.json();
    return data.distances || {};
  } catch (error) {
    console.error('Failed to fetch venue distances:', error);
    return {};
  }
}

/**
 * Fetch venue categories with icons
 * @returns {Promise<Object>} Map of category name to icon name
 */
export async function fetchCategoryIcons() {
  try {
    const response = await fetch(`${apiUrl}/loyalty/venue-categories`);
    if (!response.ok) {
      throw new Error('Failed to fetch category icons');
    }

    const data = await response.json();
    const iconMap = {};
    data.categories.forEach((cat) => {
      iconMap[cat.name] = cat.icon_name;
    });

    return iconMap;
  } catch (error) {
    console.warn('Failed to fetch category icons:', error);
    return {};
  }
}

/**
 * Get customer token from email
 * @param {string} email - Customer email
 * @returns {Promise<string>} Customer token
 */
export async function getCustomerToken(email) {
  const response = await fetch(
    `${apiUrl}/coupon/customer-token?email=${encodeURIComponent(email)}`
  );
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get customer token');
  }

  const data = await response.json();
  return data.customer_token;
}
